import { useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { useApolloClient } from '@apollo/client'
import { makeStyles } from '@mui/styles'
import { Theme } from '@eversports/react-components/design-tokens/theme'
import OnSiteIcon from '@eversports/react-components/primitives/Icon/custom-icons/OnSite'
import Icon from '@eversports/react-components/primitives/Icon'
import Flex, { VerticalFlex } from '@eversports/react-components/primitives/Flex'
import Text from '@eversports/react-components/primitives/Text'
import Placeholder from '@eversports/react-components/primitives/Placeholder'
import CircularProgress from '@eversports/react-components/primitives/CircularProgress'
import useCheckoutContext from '../../data/useCheckoutContext'
import getPhoenixUrlParams from '../../lib/get-phoenix-url-params'
import { Localized } from '../../localization/react'
import BackButton from '../BackButton'
import confirmOrder from './confirm-order'

const useStyles = makeStyles(({ borders, palette, borderRadii, layoutSpacing }: Theme) => ({
  container: {
    position: 'relative',
    marginBottom: layoutSpacing.hero,
  },
  overlay: {
    position: 'absolute',
    zIndex: 100,
    height: '100%',
    width: '100%',
  },
  paymentFormWrapper: ({ isPaymentProcessing }: { isPaymentProcessing: boolean }) => ({
    display: isPaymentProcessing ? 'none' : 'block',
  }),
  offlinePaymentLabel: {
    border: borders.strong,
    borderColor: palette.primary.main,
    borderRadius: borderRadii.default,
    padding: `${layoutSpacing.dense} ${layoutSpacing.default}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: layoutSpacing.default,
  },
}))

export function CashPayment() {
  const context = useCheckoutContext()
  const location = useLocation()
  const { orderId } = getPhoenixUrlParams(location.search)

  const classes = useStyles({ isPaymentProcessing: context.isPaymentProcessing })
  const apolloClient = useApolloClient()

  useEffect(() => {
    context.setProcessPayment(() => async () => confirmOrder({ apolloClient }, orderId!))
    context.setIsPaymentValid(true)
  }, [])

  return (
    <div className={classes.container} data-testid="payment-options-container">
      {context.isPaymentProcessing && (
        <div className={classes.overlay}>
          <Placeholder height={200} width="100%" center>
            <VerticalFlex verticalSpacing="default" alignItems="center">
              <CircularProgress />
              <Text data-testid="payment-options-payment-processing">
                <Localized id="processing-payment" />
              </Text>
            </VerticalFlex>
          </Placeholder>
        </div>
      )}
      <div className={classes.paymentFormWrapper}>
        <VerticalFlex verticalSpacing="wide">
          <label className={classes.offlinePaymentLabel}>
            <Icon size="xx-large" icon={OnSiteIcon} />
            <Flex flexDirection="column" verticalSpacing="dense">
              <Text fontWeight="bold">
                <Localized id="pay-offline" />
              </Text>
              <Text>
                <Localized id="payment-method-offline-information" />
              </Text>
            </Flex>
          </label>
          {!context.isPaymentProcessing && <BackButton abortOrderOnBackClick />}
        </VerticalFlex>
      </div>
    </div>
  )
}
